import { useDecks } from '../../hooks/useDecks';
import { Plus, Library } from 'lucide-react';
import { DeckListItem } from '../../components/decks/DeckListItem';
import { EmptyDeckState } from '../../components/decks/EmptyDeckState';
import { Link } from 'react-router-dom';
import { PageTitle } from '../../components/layout/PageTitle';
import { PageLayout } from '../../components/layout/PageLayout';
import { Skeleton } from 'antd';
import { AdSense } from '../../components/ads/AdSense';
import { useI18n } from '../../contexts/I18nContext';

const DecksSkeleton = () => {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-6">
        <Skeleton.Input active size="large" className="!w-[200px] !h-[36px]" />
        <Skeleton.Button active size="large" className="!w-[150px]" />
      </div>
      
      {/* Introduction Skeleton */}
      <div className="mb-8">
        <div className="max-w-3xl">
          <Skeleton active paragraph={{ rows: 1 }} />
        </div>
        
        {/* Statistiques Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          {[...Array(3)].map((_, index) => (
            <div key={index} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center gap-3">
                <Skeleton.Avatar active size="large" shape="circle" />
                <div className="flex-1">
                  <Skeleton active paragraph={{ rows: 1 }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Conseils Skeleton */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 mb-8">
        <Skeleton.Input active size="large" className="!w-[200px] !mb-3" />
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(3)].map((_, index) => (
            <div key={index}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </div>
      
      {/* Titre de section Skeleton */}
      <div className="flex items-center justify-between mb-5">
        <Skeleton.Input active size="large" className="!w-[150px]" />
        <Skeleton.Input active size="small" className="!w-[100px]" />
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        {[...Array(8)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-100 h-[280px]">
            <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 px-3 py-2 border-b border-indigo-100">
              <div className="flex items-center justify-between">
                <Skeleton.Input active size="small" className="!w-[60%]" />
                <Skeleton.Avatar active size="small" shape="circle" />
              </div>
            </div>
            <div className="p-3">
              <div className="flex items-center gap-1 mb-2">
                <Skeleton.Input active size="small" className="!w-[40%]" />
                <Skeleton.Input active size="small" className="!w-[30%]" />
              </div>
              <div className="bg-gray-50 p-2 rounded-lg mb-3">
                <div className="flex flex-wrap justify-center gap-1">
                  {[...Array(6)].map((_, cardIndex) => (
                    <Skeleton.Image key={cardIndex} active className="!w-[40px] !h-[56px]" />
                  ))}
                </div>
              </div>
              <Skeleton.Input active size="small" className="!w-full" />
            </div>
          </div>
        ))}
      </div>
      
      {/* FAQ Skeleton */}
      <div className="mt-10">
        <Skeleton.Input active size="large" className="!w-[200px] !mb-5" />
        <div className="grid md:grid-cols-2 gap-5">
          {[...Array(2)].map((_, index) => (
            <div key={index} className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <Skeleton.Input active size="small" className="!w-[80%] !mb-2" />
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function Decks() {
  const { decks, loading } = useDecks();
  const { t, getPathForLang } = useI18n();

  if (loading) {
    return (
      <PageLayout
        breadcrumbProps={{
          items: [
            { label: t('decks_page.title'), path: '/decks', icon: <Library className="w-4 h-4" /> }
          ]
        }}
      >
        <PageTitle title={t('decks_page.title')} />
        <DecksSkeleton />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      breadcrumbProps={{
        items: [
          { label: t('decks_page.title'), path: '/decks', icon: <Library className="w-4 h-4" /> }
        ]
      }}
    >
      <PageTitle title={t('decks_page.title')} />
      
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{t('decks_page.title')}</h1>
        <Link
          to={getPathForLang('/decks/new')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('decks_page.new_deck')}
        </Link>
      </div>

      {/* Annonce AdSense unique */}
      <div className="mt-6 mb-8">
        <AdSense adSlot="1424794127" />
      </div>

      {/* Section d'introduction */}
      <div className="mb-8">
        <div className="max-w-3xl">
          <p className="text-gray-600 mb-4">
            {t('decks_page.introduction')}
          </p>
        </div>

        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-100 p-2 rounded-full">
                <Library className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('decks_page.stats.my_decks')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('decks_page.stats.decks_created', { count: decks.length })}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('decks_page.stats.public_decks')}</h3>
                <p className="text-gray-600 text-sm">
                  {t('decks_page.stats.decks_shared', { count: decks.filter(d => d.is_public).length })}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('decks_page.stats.create_deck')}</h3>
                <Link to={getPathForLang('/decks/new')} className="text-indigo-600 text-sm hover:underline">
                  {t('decks_page.stats.start_new_strategy')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Conseils rapides */}
      {decks.length > 0 && (
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-5 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">{t('decks_page.tips.title')}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('decks_page.tips.balance_cards.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('decks_page.tips.balance_cards.description')}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('decks_page.tips.test_strategy.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('decks_page.tips.test_strategy.description')}
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">{t('decks_page.tips.share_community.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('decks_page.tips.share_community.description')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Titre de la section decks */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-800">{t('decks_page.my_collections')}</h2>
        {decks.length > 0 && (
          <div className="text-sm text-gray-500">
            {t('decks_page.total', { count: decks.length })}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
        {decks.map((deck) => (
          <DeckListItem key={deck.id} deck={deck} />
        ))}
      </div>

      {decks.length === 0 && (
        <EmptyDeckState onCreateNew={() => window.location.href = getPathForLang('/decks/new')} />
      )}

      {/* FAQ section */}
      {decks.length > 0 && (
        <div className="mt-10">
          <h2 className="text-xl font-bold text-gray-900 mb-5">{t('decks_page.faq.title')}</h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">{t('decks_page.faq.make_public.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('decks_page.faq.make_public.description')}
              </p>
            </div>
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">{t('decks_page.faq.organize_cards.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('decks_page.faq.organize_cards.description')}
              </p>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}